import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DndContext, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ChevronLeft, ChevronRight, CalendarOff, Plus, Trash2, Coffee, BookOpen,
  HeartPulse, Plane, UserMinus, Ban, Gauge, ClipboardList, Users2, Briefcase,
  AlertTriangle, Clock, Activity, Search, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import ContractDialog from "@/components/ContractDialog";
import PlanningContractCard from "@/components/PlanningContractCard";
import { useConfirm } from "@/components/ConfirmDialog";
import { useTimeFormat } from "@/hooks/useTimeFormat";

const REASONS = ["vacation", "sick", "training", "day_off", "private", "other"];
const REASON_ICONS = {
  vacation: Plane, sick: HeartPulse, training: BookOpen,
  day_off: Coffee, private: UserMinus, other: Ban,
};

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const todayStr = () => fmt(new Date());

function initials(first, last) {
  return `${(first || "?")[0]}${(last || "")[0] || ""}`.toUpperCase();
}

function AvatarBubble({ first, last, size = "md" }) {
  const cls = size === "sm" ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs";
  const palette = ["bg-blue-500/20 text-blue-300", "bg-emerald-500/20 text-emerald-300",
    "bg-amber-500/20 text-amber-300", "bg-violet-500/20 text-violet-300",
    "bg-pink-500/20 text-pink-300", "bg-cyan-500/20 text-cyan-300"];
  const key = `${first || ""}${last || ""}`;
  const sum = [...key].reduce((s, c) => s + c.charCodeAt(0), 0);
  const color = palette[sum % palette.length];
  return (
    <div className={`shrink-0 rounded-full ${cls} ${color} flex items-center justify-center font-display font-semibold`}>
      {initials(first, last)}
    </div>
  );
}

function StatMini({ Icon, label, value, sub, accent = "text-blue-400" }) {
  return (
    <div className="surface rounded-xl p-3 min-w-0" data-testid={`plan-stat-${label}`}>
      <div className="flex items-center gap-2">
        <div className={`h-7 w-7 rounded-md bg-[#171B24] flex items-center justify-center ${accent}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 truncate">{label}</div>
          <div className="font-display font-semibold text-base font-mono-stat leading-tight">{value}</div>
          {sub && <div className="text-[9px] text-zinc-500 truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function CapacityBar({ aw, cap, tf }) {
  const pct = cap ? Math.round((aw / cap) * 100) : 0;
  const color = pct > 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  const textColor = pct > 100 ? "text-red-400" : pct >= 80 ? "text-amber-400" : "text-emerald-400";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px] text-zinc-500">
        <span className="font-mono-stat text-zinc-300">{tf.format(aw)} / {tf.format(cap)}</span>
        <span className={`font-mono-stat font-semibold ${textColor}`}>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#171B24] overflow-hidden">
        <div className={`${color} h-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

/**
 * Drop-zone column. Renders contracts vertically; shows absence/day-off/break
 * overlays. `label` describes the target for screen readers + tests.
 */
function DropColumn({
  id, contracts, absence, dayOff, breaks, onAbsenceRemove,
  customersById, vehiclesById, matchingIds, minHeight = 340, cardSize = "md",
  emptyLabel, headerSlot,
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const { t } = useTranslation();
  const blocked = !!absence || dayOff;

  return (
    <div
      ref={setNodeRef}
      className={`relative flex flex-col rounded-lg bg-[#1B1F27] border border-[#2E3241] overflow-hidden transition-colors ${
        isOver ? (blocked ? "ring-2 ring-red-500/60" : "ring-2 ring-blue-500/60") : ""
      } ${blocked ? "bg-[#171B24]/60" : ""}`}
      style={{ minHeight }}
      data-testid={`drop-col-${id}`}
    >
      {headerSlot}

      {/* Break tags (top-right, only when NOT blocked) */}
      {!blocked && breaks && breaks.length > 0 && (
        <div className="absolute right-2 top-2 z-10 flex flex-col gap-1 pointer-events-none">
          {breaks.map((b, i) => (
            <span key={i} title={`${t("settings.breaks")}: ${b.start}–${b.end}`}
              className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px] font-mono-stat border border-amber-500/30">
              {b.start}–{b.end}
            </span>
          ))}
        </div>
      )}

      {/* Absence overlay (fully covers the drop area) */}
      {absence && (
        <div className="absolute inset-0 rounded-lg bg-red-500/10 border-2 border-dashed border-red-500/40 flex flex-col items-center justify-center text-center p-4 pointer-events-none">
          <div className="flex items-center gap-2 text-red-300">
            {React.createElement(REASON_ICONS[absence.reason] || Ban, { className: "h-5 w-5" })}
            <span className="text-sm font-semibold uppercase tracking-wider">
              {t(`absence.reasons.${absence.reason}`)}
            </span>
          </div>
          {absence.note && <div className="text-xs text-red-300/80 mt-2 line-clamp-3 px-2">{absence.note}</div>}
          {onAbsenceRemove && (
            <button
              onClick={(e) => { e.stopPropagation(); onAbsenceRemove(absence.id); }}
              className="absolute top-2 right-2 text-red-300/70 hover:text-red-200 pointer-events-auto"
              data-testid={`absence-remove-${absence.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {!absence && dayOff && (
        <div className="absolute inset-0 rounded-lg bg-[#171B24]/60 border-2 border-dashed border-[#2E3241] flex items-center justify-center pointer-events-none">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">{t("absence.weekend_day_off")}</span>
        </div>
      )}

      {/* Contract list */}
      <div className={`flex-1 p-2 space-y-2 overflow-y-auto ${blocked ? "opacity-30" : ""}`}>
        {contracts.length === 0 && !blocked && (
          <div className="h-full min-h-[80px] flex items-center justify-center text-[11px] text-zinc-600 uppercase tracking-wider">
            {emptyLabel || t("common.empty")}
          </div>
        )}
        {contracts.map((c) => {
          const dimmed = matchingIds && !matchingIds.has(c.id);
          return (
            <div key={c.id} className={dimmed ? "opacity-20" : (matchingIds ? "ring-2 ring-amber-400/70 rounded-lg" : "")}>
              <PlanningContractCard
                contract={c}
                customer={customersById[c.customer_id]}
                vehicle={vehiclesById[c.vehicle_id]}
                size={cardSize}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Column header used in Day view — one per mechanic. */
function EmployeeColumnHeader({ row, aw, cap, tf, onAddAbsence, absent }) {
  const { t } = useTranslation();
  return (
    <div className="px-3 py-2.5 bg-[#232732] border-b border-[#2E3241] space-y-2">
      <div className="flex items-start gap-2.5">
        <AvatarBubble first={row.employee.first_name} last={row.employee.last_name} />
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-sm truncate leading-tight" data-testid={`emp-name-${row.employee.id}`}>
            {row.employee.first_name} {row.employee.last_name}
          </div>
          <div className="text-[11px] text-zinc-500 truncate">
            {absent ? <span className="text-red-300 uppercase tracking-wider">{t("absence.reasons.vacation")}</span> : row.employee.role}
          </div>
        </div>
        {!absent && (
          <Button
            size="icon" variant="ghost"
            className="h-7 w-7 text-zinc-500 hover:text-red-300 -mr-1"
            onClick={() => onAddAbsence(row.employee)}
            data-testid={`emp-add-absence-${row.employee.id}`}
            title={t("absence.new")}
          >
            <CalendarOff className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {!absent && <CapacityBar aw={aw} cap={cap} tf={tf} />}
    </div>
  );
}

/** Column header used in Week view — one per weekday of the selected mechanic. */
function DayColumnHeader({ dateStr, weekdayLabel, aw, cap, tf, isToday }) {
  const dt = new Date(dateStr);
  return (
    <div className={`px-3 py-2.5 bg-[#232732] border-b border-[#2E3241] space-y-2 ${isToday ? "ring-1 ring-blue-500/40" : ""}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{weekdayLabel}</div>
          <div className="font-display text-lg font-semibold leading-tight">
            {dt.getDate()}.{String(dt.getMonth() + 1).padStart(2, "0")}
          </div>
        </div>
        {isToday && <span className="text-[10px] font-mono-stat text-blue-300 uppercase tracking-wider">•</span>}
      </div>
      <CapacityBar aw={aw} cap={cap} tf={tf} />
    </div>
  );
}

export default function WeeklyPlanning() {
  const { t, i18n } = useTranslation();
  const tf = useTimeFormat();
  const confirm = useConfirm();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [view, setView] = useState(() =>
    localStorage.getItem("planning:view") || "week"
  );
  const [selectedEmpId, setSelectedEmpId] = useState(() =>
    localStorage.getItem("planning:emp") || ""
  );
  const [customersById, setCustomersById] = useState({});
  const [vehiclesById, setVehiclesById] = useState({});
  const [absenceDialog, setAbsenceDialog] = useState(null);
  const [absForm, setAbsForm] = useState({ start_date: "", end_date: "", reason: "vacation", note: "" });
  const [contractDialog, setContractDialog] = useState({ open: false, empId: "", date: "" });
  const [q, setQ] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => { localStorage.setItem("planning:view", view); }, [view]);
  useEffect(() => { if (selectedEmpId) localStorage.setItem("planning:emp", selectedEmpId); }, [selectedEmpId]);

  const weekStart = useMemo(() => startOfWeek(selectedDate), [selectedDate]);
  const startStr = fmt(weekStart);
  const days = useMemo(
    () => Array.from({ length: 6 }, (_, i) => fmt(new Date(weekStart.getTime() + i * 86400000))),
    [weekStart]
  );
  const selectedDayStr = fmt(selectedDate);
  const selectedWeekdayIdx = (selectedDate.getDay() + 6) % 7;

  const load = async () => {
    const [pw, cs, vs] = await Promise.all([
      api.get("/planning/week", { params: { start: startStr } }),
      api.get("/customers"), api.get("/vehicles"),
    ]);
    setData(pw.data);
    setCustomersById(Object.fromEntries(cs.data.map((c) => [c.id, c])));
    setVehiclesById(Object.fromEntries(vs.data.map((v) => [v.id, v])));
    // Auto-select first employee for week view if none selected.
    if (!selectedEmpId && pw.data.rows && pw.data.rows.length > 0) {
      setSelectedEmpId(pw.data.rows[0].employee.id);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startStr]);

  const shiftDate = (deltaDays) => {
    const next = new Date(selectedDate);
    next.setHours(12, 0, 0, 0);
    next.setDate(next.getDate() + deltaDays);
    setSelectedDate(next);
  };
  const goToday = () => setSelectedDate(new Date());
  const onPrev = () => shiftDate(view === "day" ? -1 : -7);
  const onNext = () => shiftDate(view === "day" ? 1 : 7);
  const onDatePick = (e) => {
    const v = e.target.value;
    if (v) setSelectedDate(new Date(v + "T12:00:00"));
  };

  const onDragEnd = async (e) => {
    if (!e.over) return;
    const contractId = e.active.id;
    const overId = String(e.over.id);
    let employeeId = null, day = null;
    if (overId.startsWith("unassigned")) {
      // Drop back to unassigned pool
      try {
        await api.patch(`/contracts/${contractId}`, {
          assigned_employee_id: null,
          planned_date: null,
        });
        toast.success("OK");
        load();
      } catch { toast.error("Error"); }
      return;
    }
    [employeeId, day] = overId.split("::");
    const row = data?.rows.find((r) => r.employee.id === employeeId);
    if (row?.absence_by_day?.[day]) {
      toast.error(t("absence.blocked_assignment"));
      return;
    }
    if (row?.day_off_by_day?.[day]) {
      toast.error(t("absence.blocked_day_off"));
      return;
    }
    try {
      await api.patch(`/contracts/${contractId}`, {
        assigned_employee_id: employeeId,
        planned_date: day,
        status: "planned",
      });
      toast.success("OK");
      load();
    } catch { toast.error("Error"); }
  };

  const openAbsence = (employee) => {
    setAbsForm({ start_date: fmt(new Date()), end_date: fmt(new Date()), reason: "vacation", note: "" });
    setAbsenceDialog({ employee });
  };
  const submitAbsence = async () => {
    try {
      await api.post("/absences", { ...absForm, employee_id: absenceDialog.employee.id });
      toast.success("OK"); setAbsenceDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const removeAbsence = async (id) => {
    if (!(await confirm())) return;
    await api.delete(`/absences/${id}`); load();
  };

  const employeeNameById = useMemo(
    () => Object.fromEntries((data?.rows || []).map((r) => [r.employee.id,
      `${r.employee.first_name} ${r.employee.last_name}`])),
    [data]
  );
  const matchingIds = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || !data) return null;
    const ids = new Set();
    const searchList = [
      ...data.rows.flatMap((r) => Object.values(r.contracts_by_day || {}).flat()),
      ...(data.unassigned || []),
    ];
    for (const c of searchList) {
      const cust = customersById[c.customer_id] || {};
      const veh = vehiclesById[c.vehicle_id] || {};
      const haystack = [
        c.number, c.title, c.description, c.status, c.priority,
        cust.name, cust.phone, cust.email, cust.address,
        veh.brand, veh.model, veh.license_plate, veh.vin,
        veh.fuel_type, veh.engine,
        employeeNameById[c.assigned_employee_id] || "",
        t(`status.${c.status}`, { defaultValue: "" }),
      ].filter(Boolean).join(" ").toLowerCase();
      if (haystack.includes(s)) ids.add(c.id);
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, data, customersById, vehiclesById, employeeNameById, t]);

  if (!data) return <div className="text-zinc-400">{t("common.loading")}</div>;

  const weekdaysL = i18n.getResource(i18n.language, "translation", "weekdays") || ["Mo","Tu","We","Th","Fr","Sa","Su"];

  // ---- Summary (across visible week) ----
  const activeEmployees = data.rows.filter((r) => r.employee.active !== false).length;
  const totalCapacity = data.rows.reduce((s, r) =>
    s + days.reduce((d, k) => d + ((r.capacity_aw_by_day || {})[k] || 0), 0), 0);
  const plannedAw = data.rows.reduce((s, r) => s + days.reduce((d, k) => d + (r.aw_by_day[k] || 0), 0), 0);
  const occupancy = totalCapacity ? Math.round(plannedAw * 100 / totalCapacity) : 0;
  const contractsTotal = new Set(data.rows.flatMap((r) => Object.values(r.contracts_by_day).flat().map((c) => c.id))).size + data.unassigned.length;
  const overloaded = data.rows.filter((r) => {
    const cap = days.reduce((s, k) => s + ((r.capacity_aw_by_day || {})[k] || 0), 0);
    const aw = days.reduce((s, k) => s + (r.aw_by_day[k] || 0), 0);
    return cap > 0 && aw > cap;
  }).length;

  const selectedRow = data.rows.find((r) => r.employee.id === selectedEmpId) || data.rows[0];

  // ---- Unassigned dock (shared by day + week) ----
  const UnassignedDock = ({ title }) => {
    const { setNodeRef, isOver } = useDroppable({ id: "unassigned" });
    return (
      <div
        ref={setNodeRef}
        className={`surface rounded-xl p-3 space-y-2 transition-colors ${isOver ? "ring-2 ring-amber-400/60" : ""}`}
        data-testid="unassigned-dock"
      >
        <div className="flex items-center gap-2">
          <Inbox className="h-3.5 w-3.5 text-amber-400" />
          <div className="text-[10px] uppercase tracking-widest text-zinc-400">
            {title || t("planning.unassigned")} ({data.unassigned.length})
          </div>
        </div>
        {data.unassigned.length === 0 ? (
          <div className="text-[11px] text-zinc-600 italic py-4 text-center">
            {t("common.empty")}
          </div>
        ) : (
          <div className="space-y-2 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
            {data.unassigned.map((c) => {
              const dimmed = matchingIds && !matchingIds.has(c.id);
              return (
                <div key={c.id} className={dimmed ? "opacity-20" : (matchingIds ? "ring-2 ring-amber-400/70 rounded-lg" : "")}>
                  <PlanningContractCard
                    contract={c}
                    customer={customersById[c.customer_id]}
                    vehicle={vehiclesById[c.vehicle_id]}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="planning-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("planning.title")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("planning.title")}</h1>
          <div className="text-xs text-zinc-500 mt-1 font-mono-stat">
            {view === "day" ? selectedDayStr : `${t("planning.week_of")} ${startStr}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("common.search")}
              className="pl-9 bg-[#171B24] border-[#2E3241] h-9 w-56"
              data-testid="planning-search"
            />
          </div>
          {/* Date nav */}
          <div className="flex items-center surface rounded-lg border border-[#2E3241] p-0.5">
            <Button variant="ghost" size="sm" onClick={onPrev} className="text-zinc-300 hover:bg-[#2A2F3A] h-8" data-testid="prev-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goToday} className="text-zinc-300 hover:bg-[#2A2F3A] h-8 text-xs" data-testid="today-btn">
              {t("planning.today")}
            </Button>
            <Button variant="ghost" size="sm" onClick={onNext} className="text-zinc-300 hover:bg-[#2A2F3A] h-8" data-testid="next-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Input
              type="date"
              value={view === "day" ? selectedDayStr : startStr}
              onChange={onDatePick}
              className="bg-transparent border-0 text-xs h-8 w-[130px] pl-1"
              data-testid="date-picker"
            />
          </div>
          {/* View switcher */}
          <div className="flex items-center surface rounded-lg border border-[#2E3241] p-0.5">
            {[
              ["week", t("planning.view_week")],
              ["day", t("planning.view_day")],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                data-testid={`view-${k}`}
                className={`text-xs px-3 h-8 rounded-md transition-colors ${
                  view === k ? "bg-blue-500 text-white" : "text-zinc-400 hover:bg-[#2A2F3A]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <Button
            onClick={() => setContractDialog({ open: true, empId: "", date: "" })}
            className="bg-blue-500 hover:bg-blue-600 h-9"
            data-testid="planning-new-contract"
          >
            <Plus className="h-4 w-4 mr-1" /> {t("planning.create_contract")}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatMini Icon={Gauge} label={t("planning.sum_capacity")} value={tf.format(totalCapacity)} />
        <StatMini Icon={ClipboardList} label={t("planning.sum_planned")} value={tf.format(plannedAw)} accent="text-emerald-400" />
        <StatMini Icon={Activity} label={t("planning.sum_occupancy")} value={`${occupancy}%`}
          accent={occupancy > 100 ? "text-red-400" : occupancy >= 80 ? "text-amber-400" : "text-emerald-400"} />
        <StatMini Icon={Users2} label={t("planning.sum_active")} value={activeEmployees} />
        <StatMini Icon={Briefcase} label={t("planning.sum_contracts")} value={contractsTotal} />
        <StatMini Icon={AlertTriangle} label={t("planning.sum_overloaded")} value={overloaded} accent={overloaded > 0 ? "text-red-400" : "text-zinc-400"} />
      </div>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        {/* -------------------- DAY VIEW -------------------- */}
        {view === "day" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4" data-testid="day-view">
            <div className="overflow-x-auto pb-2">
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(data.rows.length, 1)}, minmax(280px, 1fr))`,
                  minWidth: `${data.rows.length * 300}px`,
                }}
              >
                {data.rows.map((row) => {
                  const absence = row.absence_by_day?.[selectedDayStr];
                  const dayOff = row.day_off_by_day?.[selectedDayStr];
                  const cap = (row.capacity_aw_by_day || {})[selectedDayStr] || 0;
                  const aw = row.aw_by_day[selectedDayStr] || 0;
                  return (
                    <DropColumn
                      key={row.employee.id}
                      id={`${row.employee.id}::${selectedDayStr}`}
                      contracts={row.contracts_by_day[selectedDayStr] || []}
                      absence={absence}
                      dayOff={dayOff}
                      breaks={row.breaks_by_day?.[selectedDayStr] || []}
                      onAbsenceRemove={removeAbsence}
                      customersById={customersById}
                      vehiclesById={vehiclesById}
                      matchingIds={matchingIds}
                      minHeight={520}
                      cardSize="md"
                      headerSlot={
                        <EmployeeColumnHeader
                          row={row} aw={aw} cap={cap} tf={tf}
                          onAddAbsence={openAbsence}
                          absent={!!absence}
                        />
                      }
                    />
                  );
                })}
              </div>
            </div>
            <UnassignedDock />
          </div>
        )}

        {/* -------------------- WEEK VIEW -------------------- */}
        {view === "week" && (
          <div className="space-y-3" data-testid="week-view">
            {/* Employee tab strip */}
            <div className="surface rounded-xl p-2 overflow-x-auto">
              <div className="flex items-center gap-1 min-w-max" role="tablist">
                {data.rows.map((row) => {
                  const isActive = row.employee.id === selectedRow?.employee.id;
                  return (
                    <button
                      key={row.employee.id}
                      onClick={() => setSelectedEmpId(row.employee.id)}
                      role="tab"
                      aria-selected={isActive}
                      className={`flex items-center gap-2 px-3 h-10 rounded-lg transition-colors shrink-0 ${
                        isActive
                          ? "bg-blue-500/20 text-blue-100 ring-1 ring-blue-500/60"
                          : "text-zinc-300 hover:bg-[#2A2F3A]"
                      }`}
                      data-testid={`emp-tab-${row.employee.id}`}
                    >
                      <AvatarBubble first={row.employee.first_name} last={row.employee.last_name} size="sm" />
                      <div className="text-left leading-tight">
                        <div className="text-xs font-display font-semibold">
                          {row.employee.first_name} {row.employee.last_name}
                        </div>
                        <div className="text-[10px] text-zinc-500">{row.employee.role}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedRow ? (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
                <div className="overflow-x-auto pb-2">
                  <div
                    className="grid gap-3"
                    style={{
                      gridTemplateColumns: `repeat(${days.length}, minmax(220px, 1fr))`,
                      minWidth: `${days.length * 240}px`,
                    }}
                  >
                    {days.map((d, idx) => {
                      const absence = selectedRow.absence_by_day?.[d];
                      const dayOff = selectedRow.day_off_by_day?.[d];
                      const cap = (selectedRow.capacity_aw_by_day || {})[d] || 0;
                      const aw = selectedRow.aw_by_day[d] || 0;
                      return (
                        <DropColumn
                          key={d}
                          id={`${selectedRow.employee.id}::${d}`}
                          contracts={selectedRow.contracts_by_day[d] || []}
                          absence={absence}
                          dayOff={dayOff}
                          breaks={selectedRow.breaks_by_day?.[d] || []}
                          onAbsenceRemove={removeAbsence}
                          customersById={customersById}
                          vehiclesById={vehiclesById}
                          matchingIds={matchingIds}
                          minHeight={520}
                          cardSize="lg"
                          headerSlot={
                            <DayColumnHeader
                              dateStr={d}
                              weekdayLabel={weekdaysL[idx]}
                              aw={aw} cap={cap} tf={tf}
                              isToday={d === todayStr()}
                            />
                          }
                        />
                      );
                    })}
                  </div>
                </div>
                <UnassignedDock />
              </div>
            ) : (
              <div className="text-sm text-zinc-500 py-8 text-center">
                {t("common.empty")}
              </div>
            )}
          </div>
        )}
      </DndContext>

      {/* Absence dialog */}
      <Dialog open={!!absenceDialog} onOpenChange={(o) => !o && setAbsenceDialog(null)}>
        <DialogContent className="bg-[#21252E] border-[#2E3241] text-white">
          <DialogHeader>
            <DialogTitle>
              {t("absence.new")} — {absenceDialog?.employee.first_name} {absenceDialog?.employee.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("absence.start")}</Label>
                <Input type="date" value={absForm.start_date}
                  onChange={(e) => setAbsForm({ ...absForm, start_date: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="absence-start" />
              </div>
              <div>
                <Label className="text-xs">{t("absence.end")}</Label>
                <Input type="date" value={absForm.end_date}
                  onChange={(e) => setAbsForm({ ...absForm, end_date: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="absence-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("absence.reason")}</Label>
              <Select value={absForm.reason} onValueChange={(v) => setAbsForm({ ...absForm, reason: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="absence-reason"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {REASONS.map((r) => <SelectItem key={r} value={r}>{t(`absence.reasons.${r}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("absence.note")}</Label>
              <Input value={absForm.note}
                onChange={(e) => setAbsForm({ ...absForm, note: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]" data-testid="absence-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAbsenceDialog(null)}>{t("common.cancel")}</Button>
            <Button onClick={submitAbsence} className="bg-blue-500 hover:bg-blue-600" data-testid="absence-submit">
              <Plus className="h-4 w-4 mr-1" /> {t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContractDialog
        open={contractDialog.open}
        onOpenChange={(o) => setContractDialog((s) => ({ ...s, open: o }))}
        initialEmployeeId={contractDialog.empId}
        initialDate={contractDialog.date}
        onCreated={() => load()}
      />
    </div>
  );
}
